import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import DashboardView from './components/DashboardView';
import TargetsView from './components/TargetsView';
import RevenueCollectionView from './components/RevenueCollectionView';
import TradersView from './components/TradersView';
import GroupsView from './components/GroupsView';
import TransactionsView from './components/TransactionsView';
import ReceiptsView from './components/ReceiptsView';
import UploadTradersView from './components/UploadTradersView';
import DevicesView from './components/DevicesView';
import SupportTicketsView from './components/SupportTicketsView';
import ChatButton from './components/ChatButton';
import ChatModal from './components/ChatModal';
import '../styles/views.css';

const FieldOfficerDashboard = ({ user, apiConfig }) => {
  const [selectedView, setSelectedView] = useState('Dashboard');
  const [userInfo, setUserInfo] = useState(null);
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // API Configuration
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ;

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        // Get user info from localStorage or context
        const storedUserInfo = localStorage.getItem('userInfo');
        let userData;

        if (storedUserInfo) {
          userData = JSON.parse(storedUserInfo);
        } else if (user) {
          userData = user;
        }

        if (userData) {
          // Check if zone information is already available from login response
          if (userData.zone_id && userData.zone_name) {
            // Zone information is already available from the login response
            setUserInfo(userData);
          } else if (userData.zone_error) {
            // Handle zone error from login response
            const enhancedUser = {
              ...userData,
              zone_id: null,
              zone_name: 'Zone Assignment Error',
              error: userData.zone_error
            };
            setUserInfo(enhancedUser);
          } else {
            // Fallback: Fetch zone information from revenue officers endpoint (for backward compatibility)
            try {
              // Try to find the revenue officer record that matches this field officer
              // Search by email and phone to match the field officer to their revenue officer record
              const response = await axios.get(
                `${API_BASE_URL}/revenue-officers?userRole=Revenue Officer&mmda_id=${userData.mmda_id}&email=${userData.email}&phone=${userData.phone}`,
                apiConfig
              );

              // Find the matching revenue officer record
              let matchingOfficer = null;
              if (response.data && response.data.length > 0) {
                // Try to match by email first, then by phone, then by name
                matchingOfficer = response.data.find(officer =>
                  officer.email === userData.email ||
                  officer.phone === userData.phone ||
                  officer.full_name === userData.full_name
                );

                // If no exact match, take the first one from the same MMDA
                if (!matchingOfficer) {
                  matchingOfficer = response.data[0];
                }
              }

              if (matchingOfficer && matchingOfficer.zone_id) {
                const enhancedUser = {
                  ...userData,
                  zone_id: matchingOfficer.zone_id,
                  zone_name: matchingOfficer.zone_name || `Zone ${matchingOfficer.zone_id}`,
                  mmda_name: matchingOfficer.mmda_name || userData.mmda_name,
                  officer_id: matchingOfficer.officer_id
                };
                setUserInfo(enhancedUser);
              } else {
                // Show error message to user that they need to be assigned to a zone
                const enhancedUser = {
                  ...userData,
                  zone_id: null,
                  zone_name: 'No Zone Assigned',
                  mmda_name: userData.mmda_name,
                  error: 'You have not been assigned to a zone. Please contact your administrator.'
                };
                setUserInfo(enhancedUser);
              }
            } catch (error) {
              // Show error message
              const enhancedUser = {
                ...userData,
                zone_id: null,
                zone_name: 'Zone Assignment Error',
                mmda_name: userData.mmda_name,
                error: 'Unable to fetch zone assignment. Please contact your administrator.'
              };
              setUserInfo(enhancedUser);
            }
          }
        }
      } catch (error) {
        console.error('Error in useEffect:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserDetails();
  }, [user]);

  const handleViewChange = (view) => {
    setSelectedView(view);
  };

  // Notification handlers
  const handleOpenChat = () => {
    setIsChatModalOpen(true);
  };

  const handleOpenSupport = () => {
    setSelectedView('Support');
  };

  const handleLogout = () => {
    localStorage.removeItem('userInfo');
    window.location.href = '/login';
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (!userInfo) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <div>Please log in to access the Field Officer Dashboard</div>
      </div>
    );
  }

  const renderView = () => {
    const commonProps = {
      userRole: 'Revenue Officer',
      userInfo,
      API_BASE_URL,
      apiConfig,
      auth: apiConfig,
      onViewChange: handleViewChange
    };

    switch (selectedView) {
      case 'Dashboard':
        return <DashboardView {...commonProps} />;
      case 'Targets':
        return <TargetsView {...commonProps} />;
      case 'Revenue Collection':
        return <RevenueCollectionView {...commonProps} />;
      case 'Traders':
        return <TradersView {...commonProps} />;
      case 'Groups':
        return <GroupsView {...commonProps} />;
      case 'Transactions':
        return <TransactionsView {...commonProps} />;
      case 'Receipts':
        return <ReceiptsView {...commonProps} />;
      case 'Upload Traders':
        return <UploadTradersView {...commonProps} />;
      case 'My Devices':
        return <DevicesView userInfo={userInfo} />;
      case 'Support':
        return <SupportTicketsView {...commonProps} />;
      default:
        return <DashboardView {...commonProps} />;
    }
  };

  // Show error message if user has no zone assignment
  if (userInfo && userInfo.error) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        backgroundColor: '#FFFFFFFF',
        fontFamily: 'Arial, sans-serif'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '40px',
          borderRadius: '8px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          textAlign: 'center',
          maxWidth: '500px'
        }}>
          <h2 style={{ color: '#f44336', marginBottom: '20px' }}>Zone Assignment Required</h2>
          <p style={{ color: '#666', marginBottom: '20px' }}>
            {userInfo.error}
          </p>
          <p style={{ color: '#666', fontSize: '14px', marginBottom: '20px' }}>
            Field officers must be assigned to a specific zone to access the dashboard.
          </p>
          <button
            onClick={handleLogout}
            style={{
              marginTop: '20px',
              padding: '10px 20px',
              backgroundColor: '#1976d2',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', height: '100vh' }}>
      <Sidebar
        selectedView={selectedView}
        onViewChange={handleViewChange}
        userInfo={userInfo}
        isSidebarOpen={isSidebarOpen}
        toggleSidebar={toggleSidebar}
      />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Header
          userInfo={userInfo}
          onLogout={handleLogout}
          toggleSidebar={toggleSidebar}
          onOpenChat={handleOpenChat}
          onOpenSupport={handleOpenSupport}
        />
        <main style={{
          flex: 1,
          padding: '20px',
          backgroundColor: '#FFFFFFFF',
          overflow: 'auto'
        }}>
          {renderView()}
        </main>

        {/* Chat Modal for Notification Bell */}
        <ChatModal
          isOpen={isChatModalOpen}
          onClose={() => setIsChatModalOpen(false)}
          user={userInfo}
          userRole="field_officer"
        />
      </div>
      {/* Overlay for mobile sidebar */}
      {isSidebarOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 999,
            display: window.innerWidth <= 768 ? 'block' : 'none'
          }}
          onClick={toggleSidebar}
        />
      )}

      {/* Chat Button */}
      <ChatButton user={userInfo} userRole="field_officer" />
    </div>
  );
};

export default FieldOfficerDashboard;
